import {
  addDays as addDaysFns,
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  endOfDay,
  formatISO,
  getTime,
  isSameDay,
  parse,
  parseISO,
  startOfDay,
  subDays,
  subMinutes,
} from 'date-fns';
import {
  format as DateFnsFormat,
  type OptionsWithTZ,
  utcToZonedTime,
  zonedTimeToUtc,
} from 'date-fns-tz';
import { pt } from 'date-fns/locale';

import type { TimestampInMillis } from '@cobliteam/common-types';

export type DateFns = number | Date;

export type DateRange = {
  startDate: DateFns;
  endDate: DateFns;
};

interface FormatOptions {
  showTime?: boolean;
  separator?: string;
  representation?: 'complete' | 'date' | 'time';
  dateFormat?: string;
}

// Formatters
// Formats a date to the Brazilian format
const formatDateToBr = (date: string | number, options?: FormatOptions) => {
  if (!date) return '';

  // Format date
  const { showTime, separator, dateFormat } = options || {};
  const newSeparator = separator ? ` ${separator} ` : '';
  const time = showTime ? `${newSeparator} HH:mm` : '';
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return DateFnsFormat(
    utcToZonedTime(date, currentTimezone),
    dateFormat || `dd/MM/yyyy${time}`,
    { timeZone: 'UTC' }
  );
};

const formatDateToISO = (
  date: DateFns = new Date(),
  options?: FormatOptions
) => {
  // Format date
  const { representation } = options || {};
  return formatISO(date, { representation });
};

const getISODateFromString = (date: string) => {
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Format date
  const parsedDate = parseISO(date);
  const dateInTimezone = utcToZonedTime(parsedDate, currentTimezone);
  return formatISO(dateInTimezone);
};

const formatHours = (
  hourString: string,
  inputFormat: string,
  outputFormat: string,
  date: DateFns = new Date()
) => {
  // Format date
  const parsedTime = parse(hourString, inputFormat, date);
  return DateFnsFormat(parsedTime, outputFormat);
};

/**
 * Formats a date string to UTC and applies a new format.
 * @param {string} dateAsString - The date string to be formatted.
 * @param {string} newFormat - The desired format for the date.
 * @returns {string} The formatted date string in UTC.
 * @example formatToUTC('09/06/2021', 'yyyy-MM-dd') // 2021-06-09T00:00:00.000Z
 */
const formatToUTC = (dateAsString: string, newFormat: string) => {
  const parsedDate = parse(dateAsString, newFormat, new Date());
  return DateFnsFormat(utcToZonedTime(parsedDate, 'UTC'), newFormat);
};

/**
 * Formats a date string from UTC and applies a new format.
 * @param {string} dateAsString - The date string to be formatted.
 * @param {string} newFormat - The desired format for the date.
 * @returns {string} The formatted date string from UTC.
 * @example formatFromUTC('2021-06-10T00:00:00.000Z', 'dd/MM/yyyy') // 09/06/2021
 */
const formatFromUTC = (dateAsString: string, newFormat = 'dd/MM/yyyy') => {
  const parsedDate = parse(dateAsString, newFormat, new Date());
  return DateFnsFormat(zonedTimeToUtc(parsedDate, 'UTC'), newFormat);
};

/**
 * Formats a timestamp to "Hoje", "Ontem", or a full date string with time.
 * @param {TimestampInMillis} now - The current timestamp in milliseconds.
 * @param {TimestampInMillis} lastStatus - The timestamp of the last status in milliseconds.
 * @param {string} [dateTimeSeparator='às'] - The separator between date and time.
 * @returns {string} The formatted string depending on the date difference.
 * @example formatToYesterdayOrTodayOrDate(1623345600000, 1623345600000) // Hoje às 00:00
 */
const formatToYesterdayOrTodayOrDate = (
  now: TimestampInMillis,
  lastStatus: TimestampInMillis,
  dateTimeSeparator = 'às'
) => {
  const beginOfLastStatusDay = startOfDay(lastStatus).getTime();
  const beginOfToday = startOfDay(now).getTime();
  const beginOfYesterday = startOfDay(subDays(now, 1)).getTime();

  if (isSameDay(beginOfLastStatusDay, beginOfToday)) {
    return `Hoje ${dateTimeSeparator} ${DateFnsFormat(lastStatus, 'HH:mm')}`;
  }

  if (isSameDay(beginOfLastStatusDay, beginOfYesterday)) {
    return `Ontem ${dateTimeSeparator} ${DateFnsFormat(lastStatus, 'HH:mm')}`;
  }

  return `${DateFnsFormat(
    lastStatus,
    'dd/MM/yyyy'
  )} ${dateTimeSeparator} ${DateFnsFormat(lastStatus, 'HH:mm')}`;
};

/**
 * Formats a duration in milliseconds to minutes and seconds.
 * @param {number} durationInMillis - The duration in milliseconds.
 * @returns {string} The formatted duration string with minutes and seconds.
 * @example formatDurationToMinutesAndSeconds(60000) // 1 min 0 seg
 */
const formatDurationToMinutesAndSeconds = (durationInMillis: number) => {
  const durationInSeconds = Math.floor(durationInMillis / 1000);
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds % 60;

  return `${minutes} min ${seconds} seg`;
};

// Getters
const getDateInMillis = (date: Date | string = new Date()) => {
  const formattedDate = date instanceof Date ? date : new Date(date);

  return getTime(formattedDate);
};

const getStartOfDayInMillis = (date: DateFns = new Date()) => {
  return startOfDay(date).getTime();
};

const getEndOfDayInMillis = (date: DateFns = new Date()) => {
  return endOfDay(date).getTime();
};

/**
 * Returns the start of the day in ISO string format for the given date.
 * If no date is provided, the current date is used.
 *
 * @param date - The date for which to get the start of the day.
 * @returns The start of the day in ISO string format.
 *
 * @example
 * const today = new Date();
 * const startOfDay = getStartOfDayInISOString(today);
 * console.log(startOfDay); // Output: "2022-01-01T00:00:00.000Z"
 */
const getStartOfDayInISOString = (date: DateFns = new Date()) => {
  return formatISO(startOfDay(date));
};

/**
 * Returns the end of the day in ISO string format for the given date.
 * If no date is provided, the current date is used.
 * @param date - The date for which to get the end of the day.
 * @returns The end of the day in ISO string format.
 * @example
 * const endOfDay = getEndOfDayInISOString(new Date());
 * console.log(endOfDay); // Output: "2022-01-01T23:59:59.999Z"
 */
const getEndOfDayInISOString = (date: DateFns = new Date()) => {
  return formatISO(endOfDay(date));
};

const format = (date: DateFns, formatString: string) =>
  DateFnsFormat(date, formatString);

const formatToTimezone = (
  date: DateFns,
  formatString: string,
  options: OptionsWithTZ = { locale: pt }
) => DateFnsFormat(date, formatString, options);

// Operations
const getDifferenceInHours = (
  startDate: DateFns,
  endDate: DateFns,
  roundingMethod?: string
) => {
  return differenceInHours(startDate, endDate, { roundingMethod });
};

const getDifferenceInDays = (startDate: DateFns, endDate: DateFns) => {
  return differenceInDays(startDate, endDate);
};

const getDifferenceInMinutes = (startDate: DateFns, endDate: DateFns) => {
  return differenceInMinutes(startDate, endDate);
};

const getDifferenceInMillis = (startDate: DateFns, endDate: DateFns) => {
  return differenceInMilliseconds(startDate, endDate);
};

/**
 * Adds the specified number of days to the given date.
 *
 * @param date - The date to add days to.
 * @param days - The number of days to add.
 * @returns A new Date object representing the result of the addition.
 *
 * @example
 * const currentDate = new Date();
 * const nextDate = addDays(currentDate, 7);
 * console.log(nextDate); // Output: 7 days after the current date
 */
const addDays = (date: DateFns, days: number) => {
  return addDaysFns(date, days);
};

/**
 * Subtracts the specified number of minutes from the given date.
 *
 * @param date - The date to subtract minutes from.
 * @param minutes - The number of minutes to subtract.
 * @returns The new date after subtracting the minutes.
 *
 * @example
 * const currentDate = new Date();
 * const newDate = subtractMinutes(currentDate, 30);
 * console.log(newDate); // Output: 30 minutes ago from the current date
 */
const subtractMinutes = (date: DateFns, minutes: number) => {
  return subMinutes(date, minutes);
};

/**
 * Subtracts the specified number of days from the given date.
 *
 * @param date - The date to subtract days from.
 * @param days - The number of days to subtract.
 * @returns A new Date object representing the result of the subtraction.
 *
 * @example
 * const currentDate = new Date();
 * const previousDate = subtractDays(currentDate, 7);
 * console.log(previousDate); // Output: 7 days before the current date
 */
const subtractDays = (date: DateFns, days: number) => {
  return subDays(date, days);
};

const isSameDate = (date1?: DateFns | null, date2?: DateFns | null) => {
  if (!date1 || !date2) return false;

  return isSameDay(date1, date2);
};

const isBeforeDate = (date1?: DateFns, date2?: DateFns) => {
  if (!date1 || !date2) return false;

  return date1 < date2;
};

const isAfterDate = (date1: DateFns, date2: DateFns) => {
  return date1 > date2;
};

const isBetweenDates = (
  date?: DateFns | null,
  startDate?: DateFns | null,
  endDate?: DateFns | null
) => {
  if (!date || !startDate || !endDate) return false;

  return date >= startDate && date <= endDate;
};

/**
 * Function to convert minutes to hours and minutes format.
 *
 * @param {number} minutes - The number of minutes to be converted.
 *
 * @returns {string} The converted time in 'Xh Ymin' format.
 *
 * @example
 *
 * // returns '2h 30min'
 * formatMinToHours(150);
 *
 * // returns '0h 45min'
 * formatMinToHours(45);
 *
 * // returns '1h 0min'
 * formatMinToHours(60);
 */
const formatMinToHours = (minutes: number) => {
  return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
};

export default {
  formatDateToBr,
  formatDateToISO,
  getISODateFromString,
  formatHours,
  formatMinToHours,
  format,
  formatToTimezone,
  formatToYesterdayOrTodayOrDate,
  formatDurationToMinutesAndSeconds,
  getDateInMillis,
  getStartOfDayInMillis,
  getEndOfDayInMillis,
  getStartOfDayInISOString,
  getEndOfDayInISOString,
  formatToUTC,
  formatFromUTC,
  getDifferenceInHours,
  getDifferenceInDays,
  getDifferenceInMinutes,
  getDifferenceInMillis,
  addDays,
  subtractMinutes,
  subtractDays,
  isSameDate,
  isBeforeDate,
  isAfterDate,
  isBetweenDates,
};
